/**
 * TrasabilitateTab - Componenta pentru tab-ul Trasabilitate (Audit Log)
 * Extrasă din AdminDashboard.jsx pentru modularizare și performanță
 */
import { memo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Download, Loader2 } from 'lucide-react';

// Labels pentru acțiuni în română
const ACTION_LABELS = {
  'pontaj_modificat': 'Pontaj modificat',
  'pontaj_document_upload': 'Document încărcat',
  'pontaj_document_delete': 'Document șters',
  'angajat_creat': 'Angajat creat',
  'angajat_modificat': 'Angajat modificat',
  'angajat_sters': 'Angajat șters',
  'angajat_demisie': 'Demisie setată',
  'concediu_sold_modificat': 'Sold CO modificat',
  'import_excel': 'Import Excel',
  'setare_modificata': 'Setare modificată',
  'rol_schimbat': 'Rol schimbat',
  'parola_resetata': 'Parolă resetată',
  'acces_blocat': 'Acces blocat',
  'acces_deblocat': 'Acces deblocat',
  'masina_creata': 'Mașină creată',
  'masina_modificata': 'Mașină modificată',
  'expirare_actualizata': 'Expirare actualizată'
};

/**
 * Componenta TrasabilitateTab
 * @param {Object} props
 * @param {Array} props.auditLogs - Lista de înregistrări audit
 * @param {boolean} props.auditLoading - Indicator de încărcare
 * @param {Object} props.auditFilters - Filtrele active
 * @param {Function} props.setAuditFilters - Setter pentru filtre
 * @param {Array} props.auditUsers - Lista de utilizatori pentru filtrare
 * @param {Array} props.auditActions - Lista de acțiuni pentru filtrare
 * @param {number} props.auditTotal - Total înregistrări
 * @param {number} props.auditPage - Pagina curentă
 * @param {number} props.auditTotalPages - Total pagini
 * @param {Function} props.fetchAuditLogs - Funcție pentru încărcare date
 * @param {Function} props.exportAuditLogs - Funcție pentru export Excel
 */
const TrasabilitateTab = memo(function TrasabilitateTab({
  auditLogs,
  auditLoading,
  auditFilters,
  setAuditFilters,
  auditUsers,
  auditActions,
  auditTotal,
  auditPage,
  auditTotalPages,
  fetchAuditLogs,
  exportAuditLogs
}) {
  // Format detalii pentru afișare
  const formatDetails = (details) => {
    if (!details) return '-';
    
    const d = details;
    let detailsStr = '';
    
    if (d.year && d.month && d.day) {
      detailsStr += `Ziua ${d.day}/${d.month}/${d.year}`;
    }
    if (d.old_value !== undefined && d.new_value !== undefined) {
      detailsStr += (detailsStr ? ', ' : '') + `${d.old_value} → ${d.new_value}`;
    }
    if (d.filename) {
      detailsStr += (detailsStr ? ', ' : '') + `Fișier: ${d.filename}`;
    }
    if (d.changes) {
      detailsStr = d.changes;
    }
    if (d.imported_count !== undefined) {
      detailsStr = `Importați: ${d.imported_count} angajați`;
    }
    
    return detailsStr || '-';
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleString('ro-RO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Clasă CSS pentru acțiune
  const getActionClass = (action) => {
    if (action?.includes('delete') || action?.includes('sters')) {
      return 'bg-red-100 text-red-700';
    }
    if (action?.includes('modificat')) {
      return 'bg-yellow-100 text-yellow-700';
    }
    if (action?.includes('upload') || action?.includes('creat')) {
      return 'bg-green-100 text-green-700';
    }
    return 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Jurnal Trasabilitate
              </CardTitle>
              <p className="text-sm text-slate-500 mt-1">
                Istoric modificări - cine, ce, când
              </p>
            </div>
            <Button onClick={exportAuditLogs} variant="outline" className="gap-2">
              <Download className="w-4 h-4" />
              Export Excel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Utilizator</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={auditFilters.user}
                onChange={(e) => setAuditFilters({...auditFilters, user: e.target.value})}
              >
                <option value="">Toți</option>
                {auditUsers.map(u => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Acțiune</label>
              <select
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={auditFilters.action}
                onChange={(e) => setAuditFilters({...auditFilters, action: e.target.value})}
              >
                <option value="">Toate</option>
                {auditActions.map(a => (
                  <option key={a} value={a}>{ACTION_LABELS[a] || a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">De la</label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={auditFilters.dateFrom}
                onChange={(e) => setAuditFilters({...auditFilters, dateFrom: e.target.value})}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Până la</label>
              <input
                type="date"
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={auditFilters.dateTo}
                onChange={(e) => setAuditFilters({...auditFilters, dateTo: e.target.value})}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Căutare</label>
              <input
                type="text"
                placeholder="Nume angajat..."
                className="w-full border rounded-lg px-3 py-2 text-sm"
                value={auditFilters.search}
                onChange={(e) => setAuditFilters({...auditFilters, search: e.target.value})}
              />
            </div>
          </div>
          
          {/* Results count */}
          <div className="text-sm text-slate-500 mb-2">
            Total: {auditTotal} înregistrări
          </div>
          
          {/* Table */}
          {auditLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2">Se încarcă...</span>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              Nu există înregistrări
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium border">Data/Ora</th>
                    <th className="px-3 py-2 text-left font-medium border">Utilizator</th>
                    <th className="px-3 py-2 text-left font-medium border">Acțiune</th>
                    <th className="px-3 py-2 text-left font-medium border">Angajat/Țintă</th>
                    <th className="px-3 py-2 text-left font-medium border">Detalii</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.map((log, idx) => {
                    const detailsStr = formatDetails(log.details);
                    
                    return (
                      <tr key={log.id || idx} className="hover:bg-slate-50 border-b">
                        <td className="px-3 py-2 border whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-3 py-2 border">
                          <span className="font-medium text-blue-600">{log.username}</span>
                        </td>
                        <td className="px-3 py-2 border">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${getActionClass(log.action)}`}>
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </td>
                        <td className="px-3 py-2 border font-medium">{log.target_name || '-'}</td>
                        <td className="px-3 py-2 border text-slate-600 max-w-xs truncate" title={detailsStr}>
                          {detailsStr}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Pagination */}
          {auditTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAuditLogs(auditPage - 1)}
                disabled={auditPage === 1}
              >
                ◀ Anterior
              </Button>
              <span className="text-sm text-slate-600">
                Pagina {auditPage} din {auditTotalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchAuditLogs(auditPage + 1)}
                disabled={auditPage === auditTotalPages}
              >
                Următor ▶
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
});

export default TrasabilitateTab;
